import * as R from 'ramda'
import { memo, useCallback } from 'react'
import { IconButton, Stack, styled, Typography } from '@mui/material'
import { CloseIcon } from '$common/icons/close'
import { MenuIcon } from '$common/icons/menu'
import { RemoveIcon } from '$common/icons/remove'
import { toLocale } from '$func/text'
import { exitRoomListFromSelectionMode } from '$logic/router'
import { showChatListMenu } from '$comp/modals/menus/chat-list-menu'
import { RoomList } from '$logic/room-list/room-list.react-hook'
import { getRoom } from '$logic/room'
import { getPv } from '$logic/pv'
import {
  showDeleteHallModal,
  showDeletePvModal,
  showDeleteRoomsModal,
  showLeaveHallModal,
} from '$comp/modals/confirmation-dialog'
import { hidePrivateSpace } from '$logic/private-space'
import { deleteWappRoom } from '$logic/wapp-room'
import { decodeSubscriptionPermissions } from '$logic/subscription'
import { ChatAppBarRoot } from './chat-app-bar'

interface Props {
  selectedItems: RoomList[]
}

const RowDirectionStack = styled(Stack)`
  flex-direction: row;
  align-items: center;
  justify-content: space-between;
`

const RemoveIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  margin-right: 14px;
`

const CloseIconButton = styled(IconButton)`
  width: 40px;
  height: 40px;
  margin-right: 22px;
`

export const ChatAppBarSelection = memo(function ChatAppBarSelectionComponent({
  selectedItems,
}: Props) {
  const onMenuClick = () => showChatListMenu({ chatItems: selectedItems })

  const canDelete = selectedItems.length > 0
  const onDelete = useCallback(async () => {
    await exitRoomListFromSelectionMode()
    if (selectedItems.length > 1) {
      return showDeleteRoomsModal(R.pluck('chatId', selectedItems))
    }
    const room = await getRoom(selectedItems[0].chatId)
    if (!room) return undefined
    if (room?.type === 'PV') {
      const pv = await getPv(room.id)
      return showDeletePvModal(pv.to)
    }
    if (room?.type === 'WAPP_ROOM') {
      // TODO: wait for implementing showDeleteWappRoomModel
      return deleteWappRoom(room.id, false)
    }
    if (room?.type === 'PRIVATE_SPACE') {
      // TODO: wait for implementing showDeletePrivateSpaceModel
      return hidePrivateSpace(false)
    }
    const permissions = await decodeSubscriptionPermissions(room.id)
    if (permissions.isOwner) return showDeleteHallModal(room.id)
    return showLeaveHallModal(room.id)
  }, [selectedItems])

  return (
    <ChatAppBarRoot>
      <RowDirectionStack>
        <IconButton onClick={onMenuClick}>
          <MenuIcon sx={{ color: 'neutral900' }} />
        </IconButton>
        {canDelete && (
          <RemoveIconButton onClick={onDelete}>
            <RemoveIcon sx={{ color: 'neutral900' }} />
          </RemoveIconButton>
        )}
      </RowDirectionStack>

      <RowDirectionStack>
        <Typography variant="subtitle1" color="neutral900">
          {toLocale(selectedItems.length)}
        </Typography>
        <CloseIconButton onClick={exitRoomListFromSelectionMode}>
          <CloseIcon sx={{ color: 'neutral900' }} />
        </CloseIconButton>
      </RowDirectionStack>
    </ChatAppBarRoot>
  )
})
